import React, { useState } from "react"
import { Button, Card, Chip, Grid, Typography, designTokens } from "@platform-ui/design-system";
import { Destination, ExistingDestination, Form } from "@prequel/react";
import { Box, CircularProgress, Icon } from "@mui/material";
import { BillingModelMetaData, ConnectionStatus, STATUS_CONFIG, VENDOR_TO_CONNECTOR_TITLE_MAPPING, ZDQ_VENDOR_CONNECTOR_TYPES } from "../Constants";
import { DisplayModels } from "./DisplayModels";
import { useStoreContext } from "../../../../Store";
import { IADState } from "../../../../IntegrationApps/IntegrationAppDetails/state";
import { ConnectorAuthentication } from "./ConnectorAuthentication";
import { findNested } from "../utils";

interface DestinationDetailsProps {
    vendor: ZDQ_VENDOR_CONNECTOR_TYPES;
    destinationData: ExistingDestination;
    destinationForm: Form;
    models: BillingModelMetaData[];
    setDestinationField: (key: keyof Destination, value: string | string[] | boolean | undefined) => void;
    onSubmitModelsProvision: (selectedModels: string[]) => void;
    destination: Destination;
}

const staticDetailItems = [{ key: 'id', label: 'ID' }, { key: 'is_enabled', label: 'Is Enabled' }, { key: 'vendor', label: 'Vendor' }]
const lightGrayBorder = '1px solid lightgray';

// Test Connection Component
const TestConnectionSection = ({ vendor, destinationData }: { vendor: ZDQ_VENDOR_CONNECTOR_TYPES; destinationData: ExistingDestination }) => {
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.INITIAL);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { state } = useStoreContext() as { state: IADState };
    const testConnectionUrl = `${state.settingsUrl.replace('.json', `/zdq/test_connection/${destinationData.id}`)}`;

    const config = STATUS_CONFIG[connectionStatus];
    const vendorName = VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor];

    const handleTestConnection = async () => {
        try {
            setConnectionStatus(ConnectionStatus.TESTING);
            setErrorMessage('');
            const csrf = document.querySelector('meta[name=\'csrf-token\']')?.getAttribute('content');
            const response = await fetch(testConnectionUrl, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrf || ''
                }
            });
            const res = await response.json();
            if (res?.status === 'success' && res.data?.status === 'success') {
                setConnectionStatus(ConnectionStatus.SUCCESS);
            } else {
                const errMsg = res?.message || "Unable to test connection";
                setErrorMessage(errMsg);
                setConnectionStatus(ConnectionStatus.ERROR);
            }
        } catch (e) {
            const errMsg = "Encountered an error while testing connection";
            setErrorMessage(errMsg);
            setConnectionStatus(ConnectionStatus.ERROR);
            console.log("Error while testing connection", e);
        }
    };

    return (
        <Box sx={{
            mt: 3,
            p: 3,
            border: `1px solid ${designTokens.colors.coolGray200}`,
            borderRadius: 1,
            backgroundColor: '#F8FAFC'
        }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: config.iconBgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Box sx={{
                        display: 'flex',
                        color: config.iconColor,
                        fontSize: 24,
                    }}>
                        {connectionStatus === ConnectionStatus.TESTING ? (
                            <CircularProgress thickness={5} size={22} sx={{ color: config.iconColor }} />
                        ) : (
                            <Icon sx={{ color: config.iconColor, fontSize: 24 }}>{config.iconBody}</Icon>
                        )}
                    </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant='h6'
                        body={config.heading}
                        sx={{ mb: 0.5, fontWeight: 500 }}
                    />
                    <Typography
                        variant='body2'
                        body={config.getMessage(vendorName, errorMessage)}
                        sx={{
                            color: config.buttonHoverColor,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word'
                        }}
                    />
                </Box>
            </Box>
            <Button
                dsOnClick={handleTestConnection}
                variant="filled"
                disabled={connectionStatus === ConnectionStatus.TESTING}
                sx={{
                    backgroundColor: config.buttonColor,
                    color: '#fff',
                    padding: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { backgroundColor: config.buttonHoverColor },
                    '&:disabled': { opacity: 0.7 }
                }}
                body={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 140, justifyContent: 'center', color: 'white', fontWeight: 500, fontSize: 16 }}>
                        {connectionStatus === ConnectionStatus.TESTING && <CircularProgress thickness={5} size={20} sx={{ color: "white" }} />}
                        <span>{connectionStatus === ConnectionStatus.TESTING ? 'Testing Connection...' : 'Test Connection'}</span>
                    </Box>
                }
            />
        </Box>
    );
};

export const DestinationDetails = ({ vendor, destinationData, destinationForm, models, setDestinationField, onSubmitModelsProvision, destination }: DestinationDetailsProps) => {
    const [openEditModal, setOpenEditModal] = useState(false);
    const getProvisionedModelsList = () => {
        if (destinationData?.enabled_models?.length === 1 && destinationData?.enabled_models?.toString() === ["*"].toString()) {
            return models;
        }
        return destinationData?.enabled_models?.map((modelName, index) => {
            return {
                objectName: modelName,
                id: index + 1
            } as BillingModelMetaData
        });
    }

    return <>
        <Card autoDistance
            space='large' id='connector-details'
            header={
                <Grid container justify="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant='h5'>
                            {`${VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor]} Connection `} {destinationData && <Chip label={destinationData.last_completed_transfer ? 'Active' : 'In Progress'} state={destinationData.last_completed_transfer ? 'success' : 'primary'} />}
                        </Typography>
                        {/* <>
                            <Grid container>
                                <Grid item><ConnectorMetaInfo metaDataTitle='Last Updated By' metaData='zuora@zuora.com' showBarAtEnd={true}></ConnectorMetaInfo></Grid>
                                <Grid item><Box sx={{ display: 'flex' }}><Typography variant='label' color='gray' body='|' /></Box></Grid>
                                <Grid item><ConnectorMetaInfo metaDataTitle='Last Updated Date' metaData={destinationData?.created_at} showBarAtEnd={false}></ConnectorMetaInfo></Grid>
                            </Grid>
                        </> */}
                    </Grid>
                    <Grid item>
                        <Button startIcon='edit' variant="outlined" dsOnClick={() => setOpenEditModal(true)} body='Edit' />
                    </Grid>
                </Grid>
            }
            titleBar
            body={
                <Box sx={{ width: '70%' }}>
                    <Typography variant='title' body='Details' sx={{ paddingBottom: '2%', borderBottom: `${lightGrayBorder}` }}></Typography>
                    {staticDetailItems.map((item) => {
                        return <Grid key={`destination-details-static-item-${item.key}`} container spacing={0} sx={{ paddingTop: '2%', paddingBottom: '2%', borderBottom: `${lightGrayBorder}` }}>
                            <Grid item xs={5} justify="flex-start">
                                <Typography color={designTokens.colors.coolGray500} variant='body1' body={item.label} />
                            </Grid>
                            <Grid item xs={7} justify="flex-start">
                                <Typography variant="body1" body={item.key === 'is_enabled' ? destinationData[item.key] ? 'Yes' : 'No' : destinationData[item.key]} noWrap tooltip={destinationData[item.key]?.toString()} tooltipOnlyWhenTruncated />
                            </Grid>
                        </Grid>
                    })}
                    {destinationForm?.map((section) => (
                        <Box key={`${section.id}`}>
                            {
                                section.id > 2 && <>
                                    {section?.fields?.map((field) => {
                                        return <Grid key={`destination-details-static-item-${field.name}`}
                                            container spacing={0}
                                            sx={{ paddingTop: '2%', paddingBottom: '2%', borderBottom: `${lightGrayBorder}` }}>
                                            <Grid item xs={5} justify="flex-start">
                                                <Typography color={designTokens.colors.coolGray500} variant="body1" body={field?.label || field?.name} />
                                            </Grid>
                                            <Grid item xs={7} justify='flex-start'>
                                                <Typography variant='body1' body={findNested(destinationData, field.name)?.toString()} />
                                            </Grid>
                                        </Grid>
                                    })}
                                </>
                            }
                        </Box>
                    ))}
                    <Box sx={{ marginTop: '2%' }}>
                        <DisplayModels
                            setDestinationField={setDestinationField}
                            onSubmitModelsProvision={onSubmitModelsProvision}
                            rows={getProvisionedModelsList()}
                            futureObjectsSyncOn={destinationData?.enabled_models?.length === 1 && destinationData?.enabled_models?.toString() === ['*'].toString()}
                        />
                    </Box>
                    <TestConnectionSection vendor={vendor} destinationData={destinationData} />
                </Box>
            }>
        </Card >
        {/* Edit Destination Details by reusing the ConnectorAuthentication component */}
        {openEditModal && (
            <ConnectorAuthentication
                vendor={vendor}
                models={models}
                destination={destination}
                destinationForm={destinationForm}
                setDestinationField={setDestinationField}
                isEditMode={true}
                existingDestinationData={destinationData}
                onModalClose={() => setOpenEditModal(false)}
            />
        )}
    </>
};
